import React, { useEffect, useMemo, useState } from "react";
import { gql, useQuery, useMutation } from "@apollo/client";
import Loader from "../../Loader";
import CustomSwitch from "../CommonComponents/CustomSwitch";

const GET_USER_DASHBOARD = gql`
  query getUserDashboard($data: UserDashboardInput!) {
    getUserDashboard(data: $data) {
      totalServices {
        id
        bookingId
        bookingDate
        timeSlot
        status
        verificationStatus
        price
        address
        couponCode
        couponDiscount
        codCharges
        employee {
          fullName
        }
        orderDetail {
          name
        }
      }
    }
  }
`;

const GET_USER_FLAGS = gql`
  query getUserFlags($data: UserInput!) {
    getUserlist(data: $data) {
      status
      message
      user {
        id
        isBlocked
        blockReason
        isWhatsappCampaignSuppressed
      }
    }
  }
`;

const BLOCK_USER = gql`
  mutation blockUser($data: BlockUserInput!) {
    blockUser(data: $data) {
      status
      message
    }
  }
`;

const SET_WHATSAPP_SUPPRESSION = gql`
  mutation setWhatsappCampaignSuppression(
    $data: SetWhatsappCampaignSuppressionInput!
  ) {
    setWhatsappCampaignSuppression(data: $data) {
      status
      message
    }
  }
`;

const GET_USER_WALLET = gql`
  query adminGetUserWallet($userId: ID!) {
    adminGetUserWallet(userId: $userId) {
      status
      message
      wallet {
        balance
        lifetimeEarnings
        lifetimeSpent
        isFrozen
        frozenReason
      }
      recentTransactions {
        id
        type
        category
        amount
        description
        createdAt
      }
    }
  }
`;

const ADMIN_CREDIT_WALLET = gql`
  mutation adminCreditWallet($data: AdminCreditWalletInput!) {
    adminCreditWallet(data: $data) {
      status
      message
    }
  }
`;

const ADMIN_DEBIT_WALLET = gql`
  mutation adminDebitWallet($data: AdminDebitWalletInput!) {
    adminDebitWallet(data: $data) {
      status
      message
    }
  }
`;

const WALLET_CREDIT_CATEGORIES = [
  { value: "REFERRAL_REWARD", label: "Referral Reward" },
  { value: "PROMOTIONAL", label: "Marketing Reward" },
  { value: "ADMIN_CREDIT", label: "Admin Credit" },
  { value: "CASHBACK", label: "Cashback" },
];

const WALLET_DEBIT_CATEGORIES = [
  { value: "ADMIN_DEBIT", label: "Admin Debit" },
];

const GET_USER_REFERRAL_STATS = gql`
  query adminGetUserReferralStats($userId: ID!) {
    adminGetUserReferralStats(userId: $userId) {
      status
      message
      stats {
        referralCode
        referralLink
        totalReferrals
        completedReferrals
        totalEarnings
        remainingReferrals
        referrerRewardAmount
        refereeRewardAmount
      }
    }
  }
`;

const GET_USER_REFERRAL_HISTORY = gql`
  query adminGetUserReferralHistory($userId: ID!, $page: Int, $limit: Int) {
    adminGetUserReferralHistory(userId: $userId, page: $page, limit: $limit) {
      status
      message
      referrals {
        id
        refereeName
        status
        rewardAmount
        rewardPaid
        rewardPaidAt
        createdAt
        flaggedReason
        flaggedAt
      }
    }
  }
`;

const REFERRAL_STATUS_DESCRIPTIONS = {
  PENDING: "Referee signed up, waiting for their first booking",
  ACTIVATED: "Referee completed their first booking, reward processing",
  REWARD_PENDING: "Reward approved, payout pending",
  COMPLETED: "Reward paid out",
  EXPIRED: "Referee didn't complete a booking before this referral expired",
  FLAGGED: "Flagged for fraud review",
  CANCELLED: "Referral was cancelled",
};

const referralStatusDetail = (r) => {
  const base = REFERRAL_STATUS_DESCRIPTIONS[r.status] || r.status;
  if (r.status === "FLAGGED" && r.flaggedReason) {
    return `${base}: ${r.flaggedReason}`;
  }
  return base;
};

const formatDate = (val) => {
  if (!val) return "—";
  const d = new Date(val);
  return isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
};

const statusColorMap = (status) => {
  if (!status) return "bg-gray-400";

  const s = status.toLowerCase();
  if (s.includes("cancel") || s.includes("decline"))
    return "bg-red-500 text-white";
  if (s.includes("complete")) return "bg-green-500 text-white";

  return "bg-yellow-400 text-black";
};

const InlineUserDashboard = ({ user, onBack }) => {
  const [expandedId, setExpandedId] = useState(null);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [blockReasonInput, setBlockReasonInput] = useState("");
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [creditAmountInput, setCreditAmountInput] = useState("");
  const [creditCategoryInput, setCreditCategoryInput] = useState(
    WALLET_CREDIT_CATEGORIES[0].value
  );
  const [creditDescriptionInput, setCreditDescriptionInput] = useState("");
  const [creditHasExpiry, setCreditHasExpiry] = useState(false);
  const [creditExpiryDate, setCreditExpiryDate] = useState("");
  const [creditError, setCreditError] = useState("");
  const [showDebitModal, setShowDebitModal] = useState(false);
  const [debitAmountInput, setDebitAmountInput] = useState("");
  const [debitCategoryInput, setDebitCategoryInput] = useState(
    WALLET_DEBIT_CATEGORIES[0].value
  );
  const [debitDescriptionInput, setDebitDescriptionInput] = useState("");
  const [debitError, setDebitError] = useState("");

  const { loading, data } = useQuery(GET_USER_DASHBOARD, {
    variables: { data: { userId: user.id } },
    fetchPolicy: "network-only",
  });

  const {
    data: flagsData,
    loading: flagsLoading,
    refetch: refetchFlags,
  } = useQuery(GET_USER_FLAGS, {
    variables: { data: { contactNumber: user.contactNumber } },
    fetchPolicy: "network-only",
  });

  const [runBlockUser, { loading: blockLoading }] = useMutation(BLOCK_USER);
  const [runSetWhatsappSuppression, { loading: suppressionLoading }] =
    useMutation(SET_WHATSAPP_SUPPRESSION);

  const {
    data: walletData,
    loading: walletLoading,
    refetch: refetchWallet,
  } = useQuery(GET_USER_WALLET, {
    variables: { userId: user.id },
    fetchPolicy: "network-only",
  });

  const [runCreditWallet, { loading: creditLoading }] = useMutation(
    ADMIN_CREDIT_WALLET
  );
  const [runDebitWallet, { loading: debitLoading }] = useMutation(
    ADMIN_DEBIT_WALLET
  );

  const wallet = walletData?.adminGetUserWallet?.wallet;

  const { data: referralStatsData, loading: referralStatsLoading } = useQuery(
    GET_USER_REFERRAL_STATS,
    {
      variables: { userId: user.id },
      fetchPolicy: "network-only",
    }
  );

  const { data: referralHistoryData, loading: referralHistoryLoading } =
    useQuery(GET_USER_REFERRAL_HISTORY, {
      variables: { userId: user.id, page: 1, limit: 20 },
      fetchPolicy: "network-only",
    });

  const referralStats = referralStatsData?.adminGetUserReferralStats?.stats;
  const referralHistory =
    referralHistoryData?.adminGetUserReferralHistory?.referrals || [];

  const userFlags = flagsData?.getUserlist?.user?.[0];
  const isBlocked = !!userFlags?.isBlocked;
  const isWhatsappCampaignSuppressed = !!userFlags?.isWhatsappCampaignSuppressed;

  useEffect(() => {
    setBlockReasonInput("");
  }, [showBlockModal]);

  useEffect(() => {
    setCreditAmountInput("");
    setCreditCategoryInput(WALLET_CREDIT_CATEGORIES[0].value);
    setCreditDescriptionInput("");
    setCreditHasExpiry(false);
    setCreditExpiryDate("");
    setCreditError("");
  }, [showCreditModal]);

  useEffect(() => {
    setDebitAmountInput("");
    setDebitCategoryInput(WALLET_DEBIT_CATEGORIES[0].value);
    setDebitDescriptionInput("");
    setDebitError("");
  }, [showDebitModal]);

  const handleBlockToggle = async () => {
    if (isBlocked) {
      const ok = window.confirm(
        `Unblock ${user.fullName}? Their bookings will no longer be flagged as risky.`
      );
      if (!ok) return;
      await runBlockUser({
        variables: { data: { userId: userFlags.id, isBlocked: false } },
      });
      refetchFlags();
    } else {
      setShowBlockModal(true);
    }
  };

  const confirmBlock = async () => {
    await runBlockUser({
      variables: {
        data: {
          userId: userFlags.id,
          isBlocked: true,
          reason: blockReasonInput || null,
        },
      },
    });
    setShowBlockModal(false);
    refetchFlags();
  };

  const confirmCreditWallet = async () => {
    const amount = parseFloat(creditAmountInput);
    if (!amount || amount <= 0) {
      setCreditError("Enter a valid amount greater than 0");
      return;
    }
    if (!creditDescriptionInput.trim()) {
      setCreditError("Description is required");
      return;
    }
    if (creditHasExpiry && !creditExpiryDate) {
      setCreditError("Pick an expiry date, or turn off the expiry option");
      return;
    }

    try {
      const result = await runCreditWallet({
        variables: {
          data: {
            userId: user.id,
            amount,
            category: creditCategoryInput,
            description: creditDescriptionInput.trim(),
            expiresAt:
              creditHasExpiry && creditExpiryDate
                ? new Date(`${creditExpiryDate}T23:59:59`).toISOString()
                : null,
          },
        },
      });

      if (result?.data?.adminCreditWallet?.status !== "SUCCESS") {
        setCreditError(
          result?.data?.adminCreditWallet?.message || "Failed to credit wallet"
        );
        return;
      }

      setShowCreditModal(false);
      refetchWallet();
    } catch (err) {
      setCreditError(err.message || "Failed to credit wallet");
    }
  };

  const confirmDebitWallet = async () => {
    const amount = parseFloat(debitAmountInput);
    if (!amount || amount <= 0) {
      setDebitError("Enter a valid amount greater than 0");
      return;
    }
    if (!debitDescriptionInput.trim()) {
      setDebitError("Description is required");
      return;
    }
    if (wallet && amount > wallet.balance) {
      setDebitError(
        `Amount exceeds current wallet balance (₹${wallet.balance.toFixed(2)})`
      );
      return;
    }

    try {
      const result = await runDebitWallet({
        variables: {
          data: {
            userId: user.id,
            amount,
            category: debitCategoryInput,
            description: debitDescriptionInput.trim(),
          },
        },
      });

      if (result?.data?.adminDebitWallet?.status !== "SUCCESS") {
        setDebitError(
          result?.data?.adminDebitWallet?.message || "Failed to debit wallet"
        );
        return;
      }

      setShowDebitModal(false);
      refetchWallet();
    } catch (err) {
      setDebitError(err.message || "Failed to debit wallet");
    }
  };

  const handleWhatsappSuppressionToggle = async () => {
    await runSetWhatsappSuppression({
      variables: {
        data: { userId: userFlags.id, suppressed: !isWhatsappCampaignSuppressed },
      },
    });
    refetchFlags();
  };

  /* =======================
     SORT: Latest first
  ======================= */
  const services = useMemo(() => {
    return (data?.getUserDashboard?.totalServices || [])
      .slice()
      .sort((a, b) => new Date(b.bookingDate) - new Date(a.bookingDate));
  }, [data]);

  /* =======================
     STATS
  ======================= */
  const stats = useMemo(() => {
    let verified = 0;
    let cancelled = 0;
    let declined = 0;
    let others = 0;
    let totalVerifiedAmount = 0;

    services.forEach((s) => {
      if (
        s.verificationStatus === "VERIFIED" ||
        s.verificationStatus === "PAYMENT_VERIFICATION_PENDING"
      ) {
        verified++;
        totalVerifiedAmount += s.price || 0;
      }

      const status = s.status?.toLowerCase() || "";

      if (status.includes("decline")) {
        declined++;
      } else if (status.includes("cancel")) {
        cancelled++;
      } else {
        others++;
      }
    });

    return { verified, declined, cancelled, others, totalVerifiedAmount };
  }, [services]);

  /* =======================
     FIRST VERIFIED BOOKING
  ======================= */
  const firstVerifiedBooking = useMemo(() => {
    return services
      .filter(
        (s) =>
          s.verificationStatus === "VERIFIED" ||
          s.verificationStatus === "PAYMENT_VERIFICATION_PENDING"
      )
      .sort((a, b) => new Date(a.bookingDate) - new Date(b.bookingDate))[0];
  }, [services]);

  if (loading) return <Loader />;

  return (
    <div className="space-y-6">
      {/* BACK */}
      <button
        onClick={onBack}
        className="bg-white text-black px-4 py-2 rounded-md"
      >
        ← Back to Customers
      </button>

      {/* USER SUMMARY */}
      <div className="bg-[#1f1f1f] p-4 rounded-lg text-white flex flex-col sm:flex-row sm:justify-between gap-3">
        <div>
          <h2 className="text-lg text-white font-semibold flex items-center gap-2 flex-wrap">
            {user.fullName}
            {isBlocked && (
              <span className="text-xs bg-red-600 text-white px-2 py-0.5 rounded">
                Blocked
              </span>
            )}
          </h2>
          <p className="text-sm text-gray-300">📞 {user.contactNumber}</p>
          {isBlocked && userFlags?.blockReason && (
            <p className="text-xs text-red-400 mt-1">
              Reason: {userFlags.blockReason}
            </p>
          )}
        </div>

        <div className="text-sm">
          <p className="text-gray-400">First Verified Booking</p>
          <p className="font-medium">
            {firstVerifiedBooking?.bookingDate
              ? new Date(firstVerifiedBooking.bookingDate).toLocaleDateString()
              : "—"}
          </p>
        </div>

        <div className="flex flex-col gap-3 sm:items-end">
          <button
            disabled={flagsLoading || blockLoading}
            onClick={handleBlockToggle}
            className={`px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50 ${
              isBlocked
                ? "bg-white text-black"
                : "bg-red-600 text-white"
            }`}
          >
            {isBlocked ? "Unblock User" : "Block User"}
          </button>

          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-300">
              Suppress marketing WhatsApp
            </span>
            <CustomSwitch
              checked={isWhatsappCampaignSuppressed}
              isLoading={flagsLoading || suppressionLoading}
              onChange={handleWhatsappSuppressionToggle}
              id={`whatsapp-suppress-${user.id}`}
            />
          </div>
        </div>
      </div>

      {/* WALLET */}
      <div className="bg-[#1f1f1f] p-4 rounded-lg text-white flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <p className="text-gray-400 text-sm">Wallet Balance</p>
          <p className="text-2xl font-semibold text-[#FBB034]">
            {walletLoading ? "…" : `₹${(wallet?.balance ?? 0).toFixed(2)}`}
          </p>
          {wallet?.isFrozen && (
            <p className="text-xs text-red-400 mt-1">
              Wallet frozen{wallet.frozenReason ? `: ${wallet.frozenReason}` : ""}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowDebitModal(true)}
            className="bg-white text-black px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50"
            disabled={walletLoading || !wallet?.balance}
          >
            − Debit Wallet
          </button>
          <button
            onClick={() => setShowCreditModal(true)}
            className="bg-[#FBB034] text-black px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50"
            disabled={walletLoading}
          >
            + Credit Wallet
          </button>
        </div>
      </div>

      {/* REFERRALS */}
      <div className="bg-[#1f1f1f] p-4 rounded-lg text-white space-y-4">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          <div>
            <p className="text-gray-400 text-sm">Referral Code</p>
            <p className="text-lg font-semibold text-[#FBB034]">
              {referralStatsLoading
                ? "…"
                : referralStats?.referralCode || "—"}
            </p>
          </div>
          <div className="grid grid-cols-3 gap-4 text-center sm:text-right">
            <div>
              <p className="text-xs text-gray-400">Referrals</p>
              <p className="text-lg font-semibold">
                {referralStatsLoading ? "…" : referralStats?.totalReferrals ?? 0}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-400">Completed</p>
              <p className="text-lg font-semibold text-green-400">
                {referralStatsLoading
                  ? "…"
                  : referralStats?.completedReferrals ?? 0}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-400">Earnings</p>
              <p className="text-lg font-semibold text-[#FBB034]">
                {referralStatsLoading
                  ? "…"
                  : `₹${(referralStats?.totalEarnings ?? 0).toFixed(2)}`}
              </p>
            </div>
          </div>
        </div>

        <div>
          <p className="text-xs text-gray-400 mb-2">Referral History</p>
          {referralHistoryLoading ? (
            <p className="text-sm text-gray-400">Loading…</p>
          ) : referralHistory.length === 0 ? (
            <p className="text-sm text-gray-400">No referrals yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead>
                  <tr className="text-gray-400 border-b border-gray-700">
                    <th className="py-1 pr-3 font-normal">Referred User</th>
                    <th className="py-1 pr-3 font-normal">Status</th>
                    <th className="py-1 pr-3 font-normal">What happened</th>
                    <th className="py-1 pr-3 font-normal">Reward</th>
                    <th className="py-1 pr-3 font-normal">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {referralHistory.map((r) => (
                    <tr key={r.id} className="border-b border-gray-800">
                      <td className="py-1.5 pr-3">{r.refereeName || "—"}</td>
                      <td className="py-1.5 pr-3">
                        <span
                          className={`text-xs px-2 py-0.5 rounded ${
                            r.status === "COMPLETED"
                              ? "bg-green-500 text-white"
                              : r.status === "FLAGGED" ||
                                r.status === "CANCELLED"
                              ? "bg-red-500 text-white"
                              : "bg-yellow-400 text-black"
                          }`}
                        >
                          {r.status}
                        </span>
                      </td>
                      <td className="py-1.5 pr-3 text-gray-300 max-w-xs">
                        {referralStatusDetail(r)}
                      </td>
                      <td className="py-1.5 pr-3">
                        ₹{r.rewardAmount?.toFixed(2) ?? "0.00"}{" "}
                        {r.rewardPaid ? (
                          <span className="text-green-400 text-xs">
                            (paid)
                          </span>
                        ) : (
                          <span className="text-gray-400 text-xs">
                            (unpaid)
                          </span>
                        )}
                      </td>
                      <td className="py-1.5 pr-3">{formatDate(r.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* CREDIT WALLET MODAL */}
      {showCreditModal && (
        <>
          <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
            <div className="relative w-full max-w-md my-6 mx-auto">
              <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-black outline-none focus:outline-none p-8">
                <div className="flex flex-col items-center justify-center">
                  <div
                    className="w-full flex items-center justify-end cursor-pointer text-white"
                    onClick={() => setShowCreditModal(false)}
                  >
                    <p className="w-6 h-6">×</p>
                  </div>
                  <div className="w-full flex items-center justify-center p-5 rounded-t">
                    <h3 className="text-xl font-normal text-[#FBB034]">
                      Credit Wallet
                    </h3>
                  </div>
                </div>
                <div className="flex flex-col w-full items-stretch justify-center space-y-4">
                  <p className="text-sm text-white opacity-80 text-center">
                    Add funds to {user.fullName}'s wallet.
                  </p>

                  <div>
                    <label className="text-xs text-gray-300 block mb-1">
                      Amount (₹)
                    </label>
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={creditAmountInput}
                      onChange={(e) => setCreditAmountInput(e.target.value)}
                      placeholder="e.g. 100"
                      className="w-full p-2 rounded-md text-sm"
                    />
                  </div>

                  <div>
                    <label className="text-xs text-gray-300 block mb-1">
                      Category
                    </label>
                    <select
                      value={creditCategoryInput}
                      onChange={(e) => setCreditCategoryInput(e.target.value)}
                      className="w-full p-2 rounded-md text-sm"
                    >
                      {WALLET_CREDIT_CATEGORIES.map((c) => (
                        <option key={c.value} value={c.value}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="text-xs text-gray-300 block mb-1">
                      Description
                    </label>
                    <textarea
                      value={creditDescriptionInput}
                      onChange={(e) => setCreditDescriptionInput(e.target.value)}
                      placeholder="Reason for this credit"
                      className="w-full p-2 rounded-md text-sm"
                      rows={3}
                    />
                  </div>

                  <div>
                    <label className="flex items-center gap-2 text-xs text-gray-300">
                      <input
                        type="checkbox"
                        checked={creditHasExpiry}
                        onChange={(e) => {
                          setCreditHasExpiry(e.target.checked);
                          if (!e.target.checked) setCreditExpiryDate("");
                        }}
                      />
                      Set an expiry date for these credits
                    </label>
                    {creditHasExpiry ? (
                      <input
                        type="date"
                        min={new Date().toISOString().split("T")[0]}
                        value={creditExpiryDate}
                        onChange={(e) => setCreditExpiryDate(e.target.value)}
                        className="w-full p-2 rounded-md text-sm mt-2"
                      />
                    ) : (
                      <p className="text-xs text-gray-500 mt-1">
                        No expiry — these credits will stay in the wallet indefinitely.
                      </p>
                    )}
                  </div>

                  {creditError && (
                    <p className="text-xs text-red-400">{creditError}</p>
                  )}

                  <div className="flex justify-center items-center gap-4 w-full">
                    <button
                      disabled={creditLoading}
                      onClick={confirmCreditWallet}
                      className="flex-1 bg-[#FBB034] text-black p-2 rounded-md disabled:opacity-50"
                    >
                      {creditLoading ? "Crediting…" : "Credit"}
                    </button>
                    <button
                      className="flex-1 bg-white text-black p-2 rounded-md"
                      onClick={() => setShowCreditModal(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="opacity-25 fixed inset-0 z-40 bg-black"></div>
        </>
      )}

      {/* DEBIT WALLET MODAL */}
      {showDebitModal && (
        <>
          <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
            <div className="relative w-full max-w-md my-6 mx-auto">
              <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-black outline-none focus:outline-none p-8">
                <div className="flex flex-col items-center justify-center">
                  <div
                    className="w-full flex items-center justify-end cursor-pointer text-white"
                    onClick={() => setShowDebitModal(false)}
                  >
                    <p className="w-6 h-6">×</p>
                  </div>
                  <div className="w-full flex items-center justify-center p-5 rounded-t">
                    <h3 className="text-xl font-normal text-[#FBB034]">
                      Debit Wallet
                    </h3>
                  </div>
                </div>
                <div className="flex flex-col w-full items-stretch justify-center space-y-4">
                  <p className="text-sm text-white opacity-80 text-center">
                    Remove funds from {user.fullName}'s wallet. Current
                    balance: ₹{(wallet?.balance ?? 0).toFixed(2)}
                  </p>

                  <div>
                    <label className="text-xs text-gray-300 block mb-1">
                      Amount (₹)
                    </label>
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={debitAmountInput}
                      onChange={(e) => setDebitAmountInput(e.target.value)}
                      placeholder="e.g. 100"
                      className="w-full p-2 rounded-md text-sm"
                    />
                  </div>

                  <div>
                    <label className="text-xs text-gray-300 block mb-1">
                      Category
                    </label>
                    <select
                      value={debitCategoryInput}
                      onChange={(e) => setDebitCategoryInput(e.target.value)}
                      className="w-full p-2 rounded-md text-sm"
                    >
                      {WALLET_DEBIT_CATEGORIES.map((c) => (
                        <option key={c.value} value={c.value}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="text-xs text-gray-300 block mb-1">
                      Description
                    </label>
                    <textarea
                      value={debitDescriptionInput}
                      onChange={(e) => setDebitDescriptionInput(e.target.value)}
                      placeholder="Reason for this debit"
                      className="w-full p-2 rounded-md text-sm"
                      rows={3}
                    />
                  </div>

                  {debitError && (
                    <p className="text-xs text-red-400">{debitError}</p>
                  )}

                  <div className="flex justify-center items-center gap-4 w-full">
                    <button
                      disabled={debitLoading}
                      onClick={confirmDebitWallet}
                      className="flex-1 bg-red-600 text-white p-2 rounded-md disabled:opacity-50"
                    >
                      {debitLoading ? "Debiting…" : "Debit"}
                    </button>
                    <button
                      className="flex-1 bg-white text-black p-2 rounded-md"
                      onClick={() => setShowDebitModal(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="opacity-25 fixed inset-0 z-40 bg-black"></div>
        </>
      )}

      {/* BLOCK USER MODAL */}
      {showBlockModal && (
        <>
          <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
            <div className="relative w-full max-w-md my-6 mx-auto">
              <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-black outline-none focus:outline-none p-8">
                <div className="flex flex-col items-center justify-center">
                  <div
                    className="w-full flex items-center justify-end cursor-pointer text-white"
                    onClick={() => setShowBlockModal(false)}
                  >
                    <p className="w-6 h-6">×</p>
                  </div>
                  <div className="w-full flex items-center justify-center p-5 rounded-t">
                    <h3 className="text-xl font-normal text-[#FBB034]">
                      Block User
                    </h3>
                  </div>
                </div>
                <div className="flex flex-col w-full items-center justify-center space-y-4">
                  <p className="text-sm text-white opacity-80 text-center">
                    Blocking {user.fullName} flags their future bookings so
                    admins can decide whether it's safe to send an employee.
                    This does not stop them from booking.
                  </p>
                  <textarea
                    value={blockReasonInput}
                    onChange={(e) => setBlockReasonInput(e.target.value)}
                    placeholder="Reason for blocking (optional)"
                    className="w-full p-2 rounded-md text-sm"
                    rows={3}
                  />
                  <div className="flex justify-center items-center gap-4 w-full">
                    <button
                      disabled={blockLoading}
                      onClick={confirmBlock}
                      className="flex-1 bg-red-600 text-white p-2 rounded-md disabled:opacity-50"
                    >
                      Block
                    </button>
                    <button
                      className="flex-1 bg-[#FBB034] text-black p-2 rounded-md"
                      onClick={() => setShowBlockModal(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="opacity-25 fixed inset-0 z-40 bg-black"></div>
        </>
      )}

      {/* ================= STATS ================= */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
        <div className="bg-[#1f1f1f] p-3 rounded-lg text-center">
          <p className="text-xs text-gray-400">Verified</p>
          <p className="text-lg font-semibold text-blue-400">
            {stats.verified}
          </p>
        </div>
        <div className="bg-[#1f1f1f] p-3 rounded-lg text-center">
          <p className="text-xs text-gray-400">Verified Amount</p>
          <p className="text-lg font-semibold text-blue-400">
            ₹{stats.totalVerifiedAmount}
          </p>
        </div>

        <div className="bg-[#1f1f1f] p-3 rounded-lg text-center">
          <p className="text-xs text-gray-400">Declined</p>
          <p className="text-lg font-semibold text-red-400">{stats.declined}</p>
        </div>

        <div className="bg-[#1f1f1f] p-3 rounded-lg text-center">
          <p className="text-xs text-gray-400">Cancelled</p>
          <p className="text-lg font-semibold text-red-400">
            {stats.cancelled}
          </p>
        </div>

        <div className="bg-[#1f1f1f] p-3 rounded-lg text-center">
          <p className="text-xs text-gray-400">Others</p>
          <p className="text-lg font-semibold text-yellow-400">
            {stats.others}
          </p>
        </div>
      </div>

      {/* ================= BOOKINGS GRID ================= */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {services.map((s) => {
          const isExpanded = expandedId === s.id;

          return (
            <div
              key={s.id}
              onClick={() => setExpandedId(isExpanded ? null : s.id)}
              className={`bg-[#1f1f1f] p-4 rounded-lg text-white text-sm cursor-pointer transition ${
                isExpanded ? "ring-2 ring-[#FBB034]" : ""
              }`}
            >
              {/* HEADER */}
              <div className="flex justify-between items-start">
                <p className="font-semibold truncate">{s.bookingId}</p>

                <div className="flex flex-col items-end gap-1">
                  <span
                    className={`text-xs px-2 py-0.5 rounded ${statusColorMap(
                      s.status
                    )}`}
                  >
                    {s.status}
                  </span>

                  {s.verificationStatus &&
                    s.verificationStatus !== "PENDING" && (
                      <span className="text-[10px] bg-blue-500 text-white px-2 py-0.5 rounded">
                        {s.verificationStatus}
                      </span>
                    )}
                </div>
              </div>

              {/* DATE + TIME */}
              <p className="text-gray-300 mt-1">
                📅 {new Date(s.bookingDate).toLocaleDateString()} • ⏰{" "}
                {s.timeSlot}
              </p>

              {/* PRICE */}
              <p className="font-medium mt-1">💰 ₹{s.price}</p>

              {/* COUPON */}
              {(s.couponCode || s.couponDiscount) && (
                <p className="text-green-400 text-xs">
                  🎟 {s.couponCode || "Coupon"} • ₹{s.couponDiscount || 0} off
                </p>
              )}

              {/* SERVICES */}
              <div className="flex flex-wrap gap-1 mt-2">
                {s.orderDetail.map((d, i) => (
                  <span
                    key={i}
                    className="bg-[#2a2a2a] px-2 py-0.5 rounded text-xs"
                  >
                    {d.name}
                  </span>
                ))}
              </div>

              {/* EXPANDED */}
              {isExpanded && (
                <div className="mt-3 pt-3 border-t border-gray-600 text-xs text-gray-300 space-y-1">
                  <p>
                    👤 <strong>Employee:</strong> {s.employee?.fullName || "—"}
                  </p>
                  <p>
                    📍 <strong>Address:</strong> {s.address || "—"}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default InlineUserDashboard;
